/** @jsxImportSource @emotion/react */
import React from 'react'
import PropTypes from 'prop-types'
import { Trans } from 'react-i18next'
import { jsx, css } from '@emotion/react'
import { default_theme as theme } from '../emotion/theme'
import { get, first, filter, size } from 'lodash'
import * as Yup from 'yup'
import { Row, Col } from 'react-bootstrap'
import { InlineInputField } from './form/InlineInputField'
import { FormikMultiplePhoneNumberField } from './form/MultiplePhoneNumberField'
import { BlueInlineLink } from './layout/BlueInlineLink'
import { BlueOutlineButton } from './layout/BlueOutlineButton'
import { Separator } from './layout/Separator'
import Timestamp from './Timestamp'

const blockedNumbersValidationSchema = Yup.object().shape({
  blocked_number: Yup.array().of(
    Yup.object({
      number: Yup.string().nullable().phoneNumber(),
    })
  ),
})

export const VoiceCallPlanBlockedNumbersRow = ({
  supportsBlockedNumbers,
  blockedNumbers,
  onSaveBlockedNumbers,
  lookupBlockedNumber,
  onUpgrade,
  isSaving,
  allBlockedNumbers,
}) => {
  const renderAdditionalBlockedPhoneNumberInfo = (phone_number) => {
    const blockedNumber = first(
      filter(
        allBlockedNumbers,
        (bn) => bn.blocked_number === phone_number.number
      )
    )
    if (!get(blockedNumber, 'most_recent_cdr')) {
      return null
    }
    return (
      <div css={most_recent_blocked_cdr_style}>
        &nbsp;&nbsp;&nbsp;
        <Trans>Last call</Trans>&nbsp;
        <Timestamp
          use_span={true}
          value={blockedNumber.most_recent_cdr.start_datetime}
          format="dateshort-time"
        />
      </div>
    )
  }

  const renderBlockedNumberFields = (formik_props) => {
    return (
      <div>
        <Trans>Block calls</Trans>
        <Separator variant="h10" />
        <FormikMultiplePhoneNumberField
          formik_props={formik_props}
          placeholder="Enter number to block"
          name="blocked_number"
          addButtonLabel="Block a number"
          autoCompleteLookup={lookupBlockedNumber}
          renderAdditionalPhoneNumberInfo={renderAdditionalBlockedPhoneNumberInfo}
        />
      </div>
    )
  }

  const initialValues = { blocked_number: blockedNumbers }

  return (
    <div css={[row_style, white_row_style]}>
      <div css={value_column_style}>
        <InlineInputField
          startInEditingMode={false}
          formButtonStyle="buttons"
          canEdit={supportsBlockedNumbers}
          renderRightSide={() => {
            if (supportsBlockedNumbers) {
              return null
            } else {
              return (
                <>
                  <div css={upgrade_to_unlock_notification}>
                    <Trans i18nKey={'upgrade_to_unlock_feature'}>
                      Upgrade to a paid subscription to unlock this feature
                    </Trans>
                  </div>
                  <BlueOutlineButton onClick={onUpgrade}>
                    Upgrade
                  </BlueOutlineButton>
                </>
              )
            }
          }}
          renderReadOnlyValue={({ startEditing }) => {
            return (
              <div css={readonly_row_style}>
                {supportsBlockedNumbers && (
                  <>
                    {size(blockedNumbers) === 0 && (
                      <div>
                        <Trans>Block calls</Trans>
                        &nbsp;
                        <BlueInlineLink onClick={startEditing}>
                          <Trans>Add numbers to block</Trans>
                        </BlueInlineLink>
                      </div>
                    )}
                    {size(blockedNumbers) > 0 && (
                      <div>
                        <Trans>Block calls from</Trans>
                        &nbsp;
                        <span css={readonly_blocked_numbers_list_style}>
                          {blockedNumbers[0].number}
                          {size(blockedNumbers) > 1 && (
                            <span>
                              &nbsp;+{size(blockedNumbers) - 1} <Trans>more</Trans>
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {!supportsBlockedNumbers && (
                  <div css={disabled_row_style}>
                    <Trans>Block calls</Trans>
                  </div>
                )}
              </div>
            )
          }}
          formInitialValues={initialValues}
          onSave={onSaveBlockedNumbers}
          is_saving={isSaving}
          validationSchema={blockedNumbersValidationSchema}
          renderFormFields={renderBlockedNumberFields}
        />
      </div>
    </div>
  )
}

VoiceCallPlanBlockedNumbersRow.propTypes = {
  supportsBlockedNumbers: PropTypes.any,
  blockedNumbers: PropTypes.any,
  onSaveBlockedNumbers: PropTypes.any,
  lookupBlockedNumber: PropTypes.any,
  onUpgrade: PropTypes.any,
  isSaving: PropTypes.any,
  allBlockedNumbers: PropTypes.any,
}

const row_style = css`
  display: flex;
  flex-wrap: wrap;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 15px;
  width: 100%;
  margin-left: 0px;
  margin-right: 30px;
  align-items: center;
  min-height: 68px;
`

const white_row_style = css`
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  align-items: center;
  line-height: 18px;
`

const value_column_style = css`
  padding-bottom: 15px;
  flex-grow: 1;
`

const readonly_row_style = css`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
`

const readonly_blocked_numbers_list_style = css`
  color: ${theme.colors.primary_red};
`

const most_recent_blocked_cdr_style = css`
  color: ${theme.colors.dark_grey};
`

const disabled_row_style = css`
  color: ${theme.colors.dark_grey};
`

const upgrade_to_unlock_notification = css`
  margin-right: 10px;
`


